import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { Menu, type Group, type Section } from './menu';
import { Назад } from './nazad';
import {
  useCabinets,
  useCabinetsOverview,
  useDataHealth,
  useStopState,
  useSubscriptionHint,
  useVersion,
  useWorkspaces,
  usePlanLine,
} from '../api/hooks';
import { useSession } from '../auth/session';
import { cabinetPath, useCabinetParams, запомнитьКабинет } from '../routes/params';
import { HealthBand } from './health-band';
import { PlanBand } from '../billing/plan-band';
import { WatchBand } from './watch-band';
import { Bell } from './bell';
import { LeftChip } from '../billing/left-chip';
import { HeadCabinets } from './head-cabinets';
import { isInitialTablePath } from '../api/table-startup';
import { D } from '../kabinet/dobavki';
import { Profile } from './profile';
import { SupportButton, useОткрытыеОбращения } from './support-button';
import { подписьМенюУчастников } from '../members/prava';
import { useЧислоПриглашений, useЧислоУчастников } from '../members/schyot';
import {
  BidraMark,
  IconBars2,
  IconBidder,
  IconCabinets,
  IconCard,
  IconChat,
  IconUsers,
} from '../ui/icons';
import { isStale, servedFrontendCommit, STALE_TEXT } from './stale';
import {
  имяЭкрана,
  коробкаЭкрана,
  подписьВШапке,
  подписьЭкрана,
  назадС,
  столбикЭкрана,
  type Столбик,
} from './screen';
import { K } from '../kabinet/atoms';
import { cabinetName } from '../cabinet/cabinets-text';
/* Довод об области частиц — у самого атрибута ниже. */
import '../kabinet/atoms.css';
import '../kabinet/ssylki.css';
import '../kabinet/dobavki.css';

/**
 * Общая рамка кабинета: шапка, переключатели, полосы состояния.
 *
 * ПОРЯДОК ПОЛОС СВЕРХУ ВНИЗ — ПО ВАЖНОСТИ, и он не случаен. Сначала
 * «изменения не уходят в Wildberries» (холостой ход), потом достоверность
 * данных. Человек читает сверху и останавливается на первом, что его
 * касается; если поменять местами, самое важное окажется вторым.
 */
export function Shell() {
  const params = useCabinetParams();
  const { signOut } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const workspaces = useWorkspaces();
  const cabinets = useCabinets(params?.wsId);
  const version = useVersion();

  /* ЗАПОМИНАЕМ, В КАКОМ КАБИНЕТЕ ЧЕЛОВЕК. Экрану выбора кабинета
     это нужно, чтобы нарисовать «Назад в биддер»: у него в адресе
     кабинета нет, и подставить туда первый попавшийся значило бы
     увести человека в чужой. Довод целиком — `routes/params.ts`. */
  useEffect(() => {
    if (params !== null) запомнитьКабинет(params.wsId, params.cabinetId);
  }, [params?.wsId, params?.cabinetId]);

  /**
   * ═════════════════════════════════════════════════════════════════
   * СРОК ПОДПИСКИ ВИДЕН В ЯЩИКЕ, А НЕ ТОЛЬКО НА СВОЁМ ЭКРАНЕ.
   *
   * Человек не открывает «Тариф и оплату», пока всё работает. Значит
   * узнать, что срок кончается, он может только оттуда, куда не ходит.
   * Подпись под пунктом меню — единственное место, где это попадётся
   * на глаза раньше, чем закроются разделы.
   *
   * Отказ здесь не показывается ничем: подпись — не сведение, ради
   * которого стоит ругаться на экране.
   * ═════════════════════════════════════════════════════════════════
   */
  const plan = useSubscriptionHint(params?.wsId);
  const dataHealth = useDataHealth(params?.wsId, params?.cabinetId);
  // Рубильник спрашивается отдельно: полоса «остановлено» обязана
  // держаться, даже если всё остальное на экране не прочиталось
  const stop = useStopState(params?.wsId);
  const tableCabinet = params && isInitialTablePath(location.pathname, params.wsId, params.cabinetId)
    ? params.cabinetId : undefined;
  const cabinetsOverview = useCabinetsOverview(params?.wsId, { afterTable: tableCabinet, immediate: menuOpen });

  /**
   * ПОДПИСЬ «ПОДДЕРЖКИ» — ЧИСЛО ЖДУЩИХ ОТВЕТА, как в эталоне.
   *
   * «написать нам» отвечало на вопрос, которого никто не задаёт:
   * человек и так знает, зачем идёт в поддержку. А вот «два обращения
   * ждут ответа» — это причина зайти прямо сейчас.
   *
   * Ждущих нет — остаётся приглашение: пустая подпись выглядела бы
   * как незагруженная.
   */
  // Подписи закрытого меню не задерживают таблицу. Страницы участников
  // и поддержки сами читают те же ключи; их ответы и мутации обновят
  // счётчики даже при закрытом меню. До первого чтения число неизвестно.
  const ждут = useОткрытыеОбращения(params?.wsId, menuOpen);
  const людейСДоступом = useЧислоУчастников(params?.wsId, menuOpen);
  const писемЖдёт = useЧислоПриглашений(params?.wsId, menuOpen);
  /* Подпись тарифа — «Рабочий · до 14.09.2026», как в эталоне.
     `plan` рядом остаётся: он предупреждает, а этот называет. */
  const тарифСтрокой = usePlanLine(params?.wsId);

  /**
   * ═══════════════════════════════════════════════════════════════════
   * ВСЕ ХУКИ ДО ЭТОЙ СТРОКИ — ИНАЧЕ ЭКРАН ПАДАЕТ БЕЛЫМ.
   *
   * 01.09.2026 владелец: «решил зайти в кабинеты ВБ, вот которые
   * находятся здесь, но так и не смог зайти, т.к. опять проблемы
   * со страницей… исправь здесь и пройдись по остальным страницам,
   * проверь нет ли подобных косяков, т.к. этот косяк уже не первый».
   *
   * Экран падал с `Minified React error #300` — «отрисовано меньше
   * хуков, чем ожидалось». И вот почему.
   *
   * ДВА ХУКА — `useОткрытыеОбращения` и `usePlanLine` — СТОЯЛИ НИЖЕ
   * ЭТОГО ВОЗВРАТА, среди подписей меню, где им было место по смыслу.
   * Пока в адресе была пара опознавателей, они вызывались, и React
   * запоминал десять хуков. Стоило `params` стать `null` — рамка
   * возвращалась `Navigate` после восьмого, и React обнаруживал
   * пропажу двух. Это не «иногда»: это ВСЕГДА при переходе из кабинета
   * на адрес, где кабинета нет.
   *
   * ПРАВИЛО ХУКОВ НЕ ЗНАЕТ СЛОВА «ПО СМЫСЛУ ЗДЕСЬ УМЕСТНЕЕ». Их число
   * и порядок обязаны совпадать в каждой отрисовке одного и того же
   * узла — значит все они стоят ДО единственного возврата, а подписи
   * собираются ниже из уже посчитанного.
   *
   * СТОРОЖ ПОДМЕТАЕТ ВЕСЬ КАБИНЕТ, а не этот файл: `src/rannii-vozvrat.spec.ts`
   * ищет хук после возврата в каждом компоненте дерева. Список имён
   * в стороже был бы допущением «мест ровно столько, сколько я вспомнил».
   * ═══════════════════════════════════════════════════════════════════
   */
  // В адресе не пара идентификаторов, а мусор. Возвращаем к выбору,
  // а не показываем пустой кабинет: пустой экран без объяснения читается
  // как поломка сервиса.
  if (!params) return <Navigate to="/rk" replace />;

  const webCommit = __WEB_COMMIT__;
  const stale = isStale(webCommit, servedFrontendCommit(version.data));

  const ws = workspaces.data?.find((w) => w.id === params.wsId);
  const cabinet = cabinets.data?.find((c) => c.id === params.cabinetId);

  /* ═════════════════════════════════════════════════════════════════
     КУДА ВЕДЁТ «НАЗАД» С ЭТОГО ЭКРАНА.

     С запросов — в кампанию, если она выбрана: адрес запросов несёт
     её опознаватель доводом (`?campaign=…`), и вернуть человека
     в общий список значило бы потерять ту самую кампанию, ради
     которой он и открыл запросы. Не выбрана — в биддер.
     ═════════════════════════════════════════════════════════════ */
  const назад = назадС(location.pathname);
  const домПуть = cabinetPath(params.wsId, params.cabinetId);
  const выбраннаяКампания = new URLSearchParams(location.search).get('campaign');
  const кампанияПуть =
    выбраннаяКампания === null
      ? домПуть
      : cabinetPath(params.wsId, params.cabinetId, `/campaigns/${выбраннаяКампания}`);

  // Списки пришли, и запрошенного в них нет. Значит, доступа нет —
  // ссылку прислали из чужого пространства или его отозвали.
  if (workspaces.data && !ws) return <NoAccess what="пространство" />;
  if (cabinets.data && !cabinet) return <NoAccess what="кабинет" />;

  /**
   * Разделы кабинета.
   *
   * «БИДДЕР» ВМЕСТО «ОБЗОРА»: экран называется по тому, чем на нём
   * занимаются, а не по тому, что на нём видно. «Обзор» — это про
   * смотреть; здесь же управляют кампаниями.
   */
  /**
   * Подпись под «Кабинеты Wildberries» — «3 из 5 по тарифу».
   *
   * Считает сервер (`room`), а не мы: то же число стоит в шапке,
   * в строке над таблицей и в отказе подключить лишний кабинет.
   * Четыре счёта одного числа разошлись бы обязательно.
   *
   * Потолка нет — подписи нет: «3 из ∞» не сообщает ничего.
   */
  const room = cabinetsOverview.data?.room ?? null;

  /**
   * ═══════════════════════════════════════════════════════════════════
   * ПОДПИСЬ «КАБИНЕТОВ» НАЗЫВАЕТ И ПОТОЛОК, И БЕДУ.
   *
   * В эталоне там «3 из 5 по тарифу · ключ просрочен», а у нас стояла
   * одна первая половина. Половина эта отвечает на вопрос, который
   * задают раз в месяц («сколько ещё можно подключить»), и молчит
   * о том, из-за чего кабинет стоит СЕЙЧАС.
   *
   * Считается по самому плохому ключу из всех: если сломан один
   * из трёх, человеку надо идти чинить, а какой именно — видно
   * на самом экране кабинетов.
   */
  /* ═══════════════════════════════════════════════════════════════════
     ПОДПИСЬ ПУНКТА «УЧАСТНИКИ И ПРАВА» — ТО ЖЕ ЧИСЛО, ЧТО НА ЭКРАНЕ.

     Запросы те же, что и у экрана (`['members', …]`, `['invites', …]`),
     и `react-query` их не повторяет: открытому меню число достаётся
     из того же ответа, которым живёт сам экран.

     ОТКАЗ МОЛЧИТ. Наблюдателю сервер не отдаёт приглашения — и это
     не повод показывать красную строку в меню; подпись просто
     не появится. Тот же приём, что у подписи поддержки рядом.
     ═══════════════════════════════════════════════════════════════ */
  const подписьУчастников = подписьМенюУчастников(людейСДоступом ?? 0, писемЖдёт ?? 0);

  const подписьПоддержки =
    ждут === null || ждут === 0
      ? 'написать нам'
      : `${ждут} ${склонениеОбращений(ждут)} · отвечаем в течение часа`;

  const ключи = cabinetsOverview.data?.cabinets ?? [];
  const плохойКлюч =
    ключи.find((c) => c.token !== null && c.token.health !== 'ok' && c.token.health !== 'soon') ??
    ключи.find((c) => c.token !== null && c.token.health === 'soon') ??
    null;
  const бедаКлюча =
    плохойКлюч === null
      ? null
      : плохойКлюч.token?.health === 'soon'
        ? 'ключ скоро кончится'
        : 'ключ просрочен';

  const cabinetsSub = [
    room === null || room.limit === null ? null : `${room.used} из ${room.limit} по тарифу`,
    бедаКлюча,
  ]
    .filter((ч): ч is string => ч !== null)
    .join(' · ');

  /**
   * ═══════════════════════════════════════════════════════════════════
   * МЕНЮ ПАКЕТА 25.08: ДВЕ ГРУППЫ, ПЯТЬ ПУНКТОВ ЭТАЛОНА И ДВА НАШИХ.
   *
   * Из меню УБРАНЫ: «Что сделано» (намерения), «Правила», «Запросы» —
   * это части работы с кампанией, и открываются они с её карточки.
   * «Пароль и доступ» и «Сборка» уехали в меню профиля. «Сменить
   * кабинет» стал выбором кабинета в шапке.
   *
   * ОСТАВЛЕН СВЕРХ ЭТАЛОНА ОДИН: «Участники и права». В эталоне
   * такого экрана нет вовсе, но убрать его из меню значило бы закрыть
   * работающий раздел: другого входа у него нет.
   *
   * «ЖУРНАЛ» ИЗ МЕНЮ УБРАН — ответ владельца на вопрос 98 (26.08):
   * «участники и права не убираем, журнал убираем».
   *
   * САМ ЭКРАН ОСТАЛСЯ, и это не самоволие. Журнал пространства —
   * требование ТЗ (FR-19.1, FR-19.3), и отвечает он на вопросы,
   * которые задают: «что сервис сегодня наделал», «покажите ошибки
   * за неделю». Убран ПУНКТ МЕНЮ, а вход переехал туда, где он
   * и читается по смыслу, — строкой под списком намерений
   * (`pages/intents.page.tsx`): «Намерения» говорят, что сервис
   * собирался сделать, журнал — что он сделал. Это соседи, а не
   * два раздела.
   * ═══════════════════════════════════════════════════════════════════
   */
  const groups: Group[] = [
    {
      caption: 'Разделы сервиса',
      items: [
        {
          to: cabinetPath(params.wsId, params.cabinetId),
          title: 'Биддер',
          sub: 'Кампании, ставки, часы и запросы',
          icon: <IconBidder />,
          end: true,
        },
        {
          /**
           * ═══════════════════════════════════════════════════════════
           * ВТОРОЙ РАЗДЕЛ СЕРВИСА — И С 13.09.2026 ОН ССЫЛКА.
           *
           * Здесь стояла пометка «скоро» и адреса не было вовсе:
           * раздел входил в тарифы, а экранов у него не было. Теперь есть —
           * пять вкладок, светофор, выгрузка, — и метка «в разработке»
           * стала бы врать в обратную сторону: человек, заплативший
           * за тариф, видел бы «скоро» рядом с работающим разделом.
           *
           * АДРЕС ОТНОСИТЕЛЬНЫЙ, как у соседних пунктов: он ведёт
           * внутрь текущего кабинета, а не в пространство.
           * ═══════════════════════════════════════════════════════ */
          to: 'fin',
          title: 'Финансовая аналитика',
          sub: 'Себестоимость, прибыль, отчёты',
          icon: <IconBars2 />,
        },
      ],
    },
    {
      caption: 'Пространство',
      items: [
        {
          /* ═══════════════════════════════════════════════════════
             АДРЕС РАЗДЕЛА — `settings/cabinets`, И ЭТО ВТОРАЯ ПОЛОВИНА
             ТОЙ ЖЕ ПОЛОМКИ (01.09.2026).

             Здесь стояло `/rk/${'${params.wsId}'}/cabinets` — адрес,
             которого в разборе путей НЕТ. Он попадал в правило
             `/rk/:wsId/:cabinetId`, где вместо опознавателя кабинета
             оказывалось слово «cabinets»; рамка не узнавала пару
             и уходила в возврат — тот самый, после которого стояли
             два хука.

             То есть падение было не случайностью, а прямым следствием
             нажатия на этот пункт меню.

             ВТОРАЯ ПРАВКА, 02.09.2026: адрес был верный, а рамка чужая.
             `/rk/:wsId/settings/cabinets` открывается в `AccountShell` —
             со своей шапкой и своим рядом вкладок, без меню кабинета.
             Владелец: «страница кабинеты вб вообще кривая». Раздел
             заведён и внутри кабинета (`app.tsx`), и пункт ведёт туда:
             относительный адрес, как у соседних «Счетов и оплат»
             и «Участников».
             ═══════════════════════════════════════════════════════ */
          to: 'cabinets',
          title: 'Кабинеты Wildberries',
          sub: cabinetsSub === '' ? undefined : cabinetsSub,
          icon: <IconCabinets />,
        },
        { to: 'plan', title: 'Счета и оплаты', sub: тарифСтрокой, icon: <IconCard /> },
        /* ═══════════════════════════════════════════════════════════
           МЕЖДУ СЧЕТАМИ И ПОДДЕРЖКОЙ — ТАК В ПЕРЕДАЧЕ 10.09.2026.

           Стоял он последним и без подписи. Порядок в передаче назван
           прямо («в группе „Пространство", между „Счетами и оплатами"
           и „Поддержкой"»), и он осмысленный: кабинеты — счета — люди
           идут от того, чем работают, к тому, кто работает, а поддержка
           закрывает список как выход наружу.
           ═══════════════════════════════════════════════════════ */
        {
          to: 'members',
          title: 'Участники и права',
          sub: подписьУчастников,
          icon: <IconUsers />,
        },
        {
          to: 'support',
          title: 'Поддержка',
          /* В эталоне здесь «2 обращения · отвечаем в течение часа».
             Число берётся оттуда же, откуда счётчик на значке шапки:
             два счёта одного и того же однажды разойдутся на глазах. */
          sub: подписьПоддержки,
          icon: <IconChat />,
        },
      ],
    },
  ];

  const sections = groups.flatMap((g) => g.items);

  // ТЕКУЩИЙ РАЗДЕЛ ВИДЕН НА КНОПКЕ. Меню, которое надо открыть, чтобы
  // узнать, где ты находишься, открывают каждый раз.
  // Раздел «скоро» не бывает текущим: на него нельзя перейти,
  // и подпись «Сейчас: Финансовая аналитика» была бы неправдой
  /**
   * ═══════════════════════════════════════════════════════════════════
   * НЕИЗВЕСТНЫЙ АДРЕС НЕ НАЗЫВАЕТСЯ ПЕРВЫМ РАЗДЕЛОМ.
   *
   * `?? sections[0].title` давал «Биддер» на любом адресе, которого
   * в кабинете нет: на экране «404 Такой страницы в кабинете нет»,
   * а в шапке — «Биддер». Рамка называла себя одним, содержимое
   * говорило обратное.
   *
   * Имя экрана мы уже считаем (`подписьЭкрана`), и на неизвестном
   * адресе оно честно отвечает «Не найдено». Берём его — но только
   * в качестве запасного: у известных разделов имя в меню и имя
   * экрана могут не совпадать нарочно («Добавить на телефон»
   * в меню против «На телефон» в шапке), и первым словом должно
   * оставаться то, что человек нажимал.
   * ═══════════════════════════════════════════════════════════════ */
  const раздел = sections.find(
    (x) => x.end !== true && x.soon !== true && location.pathname.startsWith(x.to),
  );
  const currentSection = раздел?.title ?? подписьЭкрана(location.pathname);
  /* В ШАПКЕ — СЛОВА ЭТАЛОНА. У раздела меню имя своё и оно сильнее
     (см. довод выше), а вот запасное берётся из `подписьВШапке`:
     эталон пишет «Поисковые запросы» и «Правила исключения» там,
     где ключ у нас короче. */
  const имяВШапке = раздел?.title ?? подписьВШапке(location.pathname);

  return (
    /* ═══════════════════════════════════════════════════════════════
       `data-kab` НА КОРНЕ — ЭТО ОБЛАСТЬ ЧАСТИЦ, А НЕ УКРАШЕНИЕ.

       Правила частиц кабинета записаны как `[data-kab] .k-…`. Без
       атрибута ни одно не срабатывает — и до сегодня так и было:
       `web/src/kabinet/atoms.css` собирался из эталона, стерёгся
       сторожем побайтово и НЕ ПРИМЕНЯЛСЯ НИ К ЧЕМУ. Атрибута не было
       нигде в кабинете, а сам файл подключался из одного `bills.tsx`.

       Вскрыла это сверка кабинета (`probe/kabinet.mjs`): узлы,
       у которых частица давно готова, расходились с эталоном
       на десяток значений каждый — поле 12 вместо 20, зазор 8 вместо
       16, радиус 8 вместо 12, тень первой ступени вместо второй.
       ═══════════════════════════════════════════════════════════ */
    <div data-kab className="flex h-full flex-col">
      {/* ШАПКА — 52 px, РОВНО КАК В ОБРАЗЦЕ. Прежде здесь стояло 56
          и отступы «на глаз»: высота, шаг и цвет задавались тут же
          числами, и с образцом они разошлись. Теперь всё это — один
          класс `rk-app-top`, и поменять высоту можно только в одном
          месте, где она и записана.

          Слева опознание («где я»), справа обиход («тема, сборка,
          выход»), между ними пусто: середина шапки — самое заметное
          место экрана, и занимать его нечем. */}
      <header className={K.шапкаКабинета} data-d="шапка-кабинета">
        <Menu
          groups={groups}
          current={currentSection}
          cabinet={cabinet ? cabinetTitle(cabinet) : undefined}
          onOpenChange={setMenuOpen}
        />

        {/* ЗНАК И НАЗВАНИЕ — ОДНОЙ ССЫЛКОЙ ДОМОЙ. Знак берётся файлом
            из public, а не вставляется в разметку: то же изображение
            стоит на входе и в значке вкладки, и три копии одного контура
            однажды разъехались бы. */}
        <Link
          to="/rk"
          className="rk-head-brand flex shrink-0 items-center gap-2.5 no-underline"
          aria-label="БИДРА"
          style={{ color: 'var(--ink)' }}
        >
          {/* ЗНАК И НАЗВАНИЕ, БЕЗ ПОДПИСИ. В эталоне под «БИДРА» ничего
              нет: подпись «кабинет рекламы» отвечает на вопрос, который
              человек задаёт один раз в жизни, а место в шапке занимает
              каждый день — и тянет её выше пятидесяти двух пикселей. */}
          <BidraMark size={26} />
          <b style={{ letterSpacing: '-.01em' }}>БИДРА</b>
        </Link>

        {/* ═══════════════════════════════════════════════════════════
            ВЫБОР КАБИНЕТА — КНОПКА С ВЫПАДЕНИЕМ (пакет 25.08).

            Здесь стояло СЕМЬ узлов на одно и то же: строка с именем
            кабинета, `select` пространства, `select` кабинета, вторая
            строка с тем же именем «для тех, у кого он один», и рядом
            название раздела. На снимке рядом с эталоном имя кабинета
            читалось дважды подряд — первое, что видно.

            Осталась одна кнопка. Пространство из шапки убрано вовсе:
            в эталоне его нет, а переключают его на `/rk`.
            ═══════════════════════════════════════════════════════════ */}
        <HeadCabinets wsId={params.wsId} cabinetId={params.cabinetId} afterTable={tableCabinet !== undefined} />

        <span className={`${K.разделВШапке} ${D.неНаТелефоне}`}>{имяВШапке}</span>

        {/* `rk-head-icons` — ради зазора 4 на 390 (ответ 34): значки
            там вырастают до сорока, и восьмипиксельный зазор между
            ними перестаёт помещаться в ширину экрана. */}
        <div className="rk-head-icons ml-auto flex shrink-0 items-center gap-2">
          {/* ═══════════════════════════════════════════════════════
              ПОРЯДОК СПРАВА — ИЗ ЭТАЛОНА, И ОН НЕ ПРОИЗВОЛЬНЫЙ.

              Колокольчик · поддержка · остаток · продлить · профиль.
              Сначала то, что сервис говорит человеку (уведомления),
              потом то, чем человек отвечает сервису (поддержка),
              потом деньги, и в самом краю — профиль, куда ходят реже
              всего. У нас капсула стояла первой: остаток подписки
              оказывался левее уведомления о том, что кампания встала.
              ═══════════════════════════════════════════════════════ */}
          <Bell wsId={params.wsId} />
          <SupportButton открытых={ждут} />

          {/* РАЗОШЕДШАЯСЯ СБОРКА ЗАНИМАЕТ МЕСТО ОСТАТКА: пока браузер
              держит старый пакет, всё на экране может врать — включая
              саму капсулу остатка. */}
          {stale ? (
            <Link
              to="/rk/version"
              title="Пакет в браузере старше того, что на сервере"
              className="rk-chip no-underline"
              style={{ background: 'var(--bad-wash)', color: 'var(--bad-ink)' }}
            >
              {webCommit} → {servedFrontendCommit(version.data) ?? 'unknown'}
            </Link>
          ) : (
            <LeftChip wsId={params.wsId} />
          )}

          <Profile />
        </div>
      </header>

      {/* СТАРЫЙ ПАКЕТ — ПЕРВОЙ ПОЛОСОЙ, выше холостого хода. Пока браузер
          держит старую сборку, всё остальное на экране может врать:
          и полосы состояния, и числа, и сами кнопки. */}
      {stale && (
        <div
          role="alert"
          className="flex items-center gap-2 px-5 py-2"
          style={{
            fontSize: 'var(--fs-sm)', lineHeight: 'var(--lh-sm)',
            background: 'var(--bad-wash)',
            color: 'var(--bad-ink)',
            borderBottom: 'var(--hair) solid var(--line)',
          }}
        >
          {STALE_TEXT}
          <button className="rk-link ml-2" onClick={() => window.location.reload()}>
            обновить
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          ХОЛОСТОЙ ХОД ВИДЕН ВСЕГДА, когда он включён. Это первый вопрос,
          который возникает у человека: «почему биддер ничего не делает».

          ЗНАЧЕНИЕ БЕРЁТСЯ ИЗ ДОСТОВЕРНОСТИ ДАННЫХ, А НЕ ИЗ СБОРКИ.
          Сборка запрашивается один раз за загрузку страницы — она
          и не меняется. Холостой ход теперь аварийный рубильник:
          его дёргают на ходу, и вкладка, открытая утром, показывала
          бы вчерашнее. Достоверность переспрашивается раз в минуту.

          Сборка осталась запасным ответом: пока достоверность
          не пришла, лучше сказать по старому значению, чем молчать.
          ═══════════════════════════════════════════════════════════ */}
      {(dataHealth.data?.dryRun ?? version.data?.dryRun) && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 px-5 py-2"
          style={{
            fontSize: 'var(--fs-sm)', lineHeight: 'var(--lh-sm)',
            background: 'var(--brand-wash)',
            color: 'var(--brand)',
            borderBottom: 'var(--hair) solid var(--line)',
          }}
        >
          Холостой ход: изменения не уходят в Wildberries.{' '}
          {/* Полоса, которая только сообщает, за месяц становится частью
              обоев. Ссылка превращает её в вопрос, на который есть ответ. */}
          <Link className="rk-link" to={cabinetPath(params.wsId, params.cabinetId, '/intents')}>
            посмотреть, что он собирался сделать
          </Link>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          АВАРИЙНАЯ ОСТАНОВКА (FR-10.3) — ВЫШЕ ДАЖЕ КЛЮЧА.

          Всё остальное в этом ряду сервис сообщает о себе: ключ
          негоден, подписка кончается, данные несвежие. Эта полоса
          сообщает о том, что сделал САМ ЧЕЛОВЕК, — и она первая,
          потому что объясняет остальные.

          Без неё экран выглядит так: ставки не меняются, расписание
          молчит, и ни одна полоса не говорит почему. Человек идёт
          искать поломку в том, что работает исправно.
          ═══════════════════════════════════════════════════════════ */}
      {stop.data?.stopped === true && (
        <div
          role="alert"
          className="rk-band flex-wrap"
          style={{
            background: 'var(--bad-wash)',
            color: 'var(--bad-ink)',
            borderBottomColor: 'color-mix(in oklab, var(--bad) 30%, transparent)',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" style={{ flex: '0 0 auto' }}>
            <path d="M8 1.5 15 14H1zM7.2 6h1.6v4H7.2zm0 5h1.6v1.6H7.2z" />
          </svg>
          {stop.data.text}{' '}
          {/* ДОРОГА К ПОЧИНКЕ — ТУДА ЖЕ, ГДЕ КНОПКА. Полоса, после
              которой надо искать нужный экран, читается как реклама */}
          <Link className="rk-link" to={`/rk/${params.wsId}/settings/cabinets`} style={{ color: 'inherit' }}>
            включить обратно
          </Link>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          КЛЮЧ WILDBERRIES — ВЫШЕ ВСЕГО ОСТАЛЬНОГО (ТЗ 13.3).

          Отозванный или истёкший ключ останавливает биддер целиком:
          ставки не меняются, счета не пополняются, расписание
          не срабатывает. Экраны при этом выглядят обычно — данные
          в базе лежат, — и человек узнаёт об этом по счёту
          от Wildberries.

          Выше подписки: кончившаяся подписка — это «через два дня
          перестанет», негодный ключ — «уже не работает».
          ═══════════════════════════════════════════════════════════ */}
      {dataHealth.data?.token?.text && (
        <div
          role="status"
          className="rk-band flex-wrap"
          style={
            dataHealth.data.token.health === 'soon'
              ? {
                  background: 'var(--warn-wash)',
                  color: 'var(--warn-ink)',
                  borderBottomColor: 'color-mix(in oklab, var(--warn) 30%, transparent)',
                }
              : undefined
          }
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" style={{ flex: '0 0 auto' }}>
            <path d="M8 1.5 15 14H1zM7.2 6h1.6v4H7.2zm0 5h1.6v1.6H7.2z" />
          </svg>
          {dataHealth.data.token.text}{' '}
          {/* ПРЯМАЯ ССЫЛКА НА ЗАМЕНУ, как требует ТЗ: сообщение
              без дороги к починке — половина сообщения */}
          <Link className="rk-link" to={`/rk/${params.wsId}/connect`} style={{ color: 'inherit' }}>
            вставить новый ключ
          </Link>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          ЧЬИ ЭТО ДАННЫЕ — ВЫШЕ ВСЕГО ОСТАЛЬНОГО.

          Остальные полосы говорят о самих данных: подписка кончается,
          выгрузка несвежая, ключ негоден. Эта говорит, ЧЬИ ОНИ.
          Сотрудник, забывший, что смотрит чужое пространство, разбирает
          чужие кампании как свои — и никакая полоса ниже этого
          не исправит.
          ═══════════════════════════════════════════════════════════ */}
      <WatchBand />

      {/* ═══════════════════════════════════════════════════════════
          СРОК ПОДПИСКИ — ВЫШЕ ДОСТОВЕРНОСТИ ДАННЫХ.

          Несвежие данные — это «числам ниже не верьте»; кончившаяся
          подписка — это «через два дня сервис перестанет работать».
          Порядок полос и есть порядок срочности.
          ═══════════════════════════════════════════════════════════ */}
      <PlanBand wsId={params.wsId} />

      <HealthBand wsId={params.wsId} cabinetId={params.cabinetId} />

      {/* ИМЯ ЭКРАНА — НА РАМКЕ, А НЕ НА СОДЕРЖИМОМ: половина страниц
          начинается с раннего возврата («читаю тариф…», «нет доступа»),
          и на них узла бы не было вовсе. Довод целиком — в `screen.ts`. */}
      {/* ПОЛЯ СТРАНИЦЫ — ЗДЕСЬ, А НЕ В КАЖДОЙ СТРАНИЦЕ. Так в эталоне:
          отступы держит сам узел экрана. У нас их отбивала каждая
          страница по-своему — `p-3` тут, `padding: var(--sp-6)` там, —
          и тринадцать экранов имели тринадцать разных верхних полей,
          ни одно из которых не совпадало с замером. Числа —
          в `screen.ts`, сверены с замером сторожем. */}
      <main
        /**
         * ═══════════════════════════════════════════════════════════
         * `rk-screen-column` — «ЭТО СТОЛБИК, И ОН НЕ СЖИМАЕТ ДЕТЕЙ».
         *
         * Коробка экрана бывает двух видов (`screen.ts`): ПОЛОСА —
         * гибкий столбик, СТОЛБЕЦ — гибкий ряд с выравниванием
         * по центру. У ПОЛОСЫ гибкая ось — ВЕРТИКАЛЬНАЯ, и `flex-shrink`
         * там работает по высоте: содержимого больше, чем экрана, —
         * и все дети ужимаются.
         *
         * Как это выглядело: кнопка «Назад» ростом 40 (палец) выходила
         * двадцать на экране «Правила». Не всегда, а только когда
         * правил в списке хватало, чтобы вылезти за край окна, —
         * то есть на живых данных и не на снимке.
         *
         * У СТОЛБЦА гибкая ось горизонтальная, и запрет сжатия там
         * означал бы горизонтальную полосу прокрутки на узком окне.
         * Поэтому класс ставится только столбику.
         * ═══════════════════════════════════════════════════════ */
        className={
          'min-h-0 flex-1 overflow-auto' +
          (коробкаЭкрана(location.pathname).flexDirection === 'column'
            ? ' rk-screen-column'
            : '')
        }
        data-d={имяЭкрана(location.pathname)}
        data-screen-label={подписьЭкрана(location.pathname)}
        style={коробкаЭкрана(location.pathname)}
      >
        {/* ═══════════════════════════════════════════════════════
            «НАЗАД» — ПЕРВЫМ В СТОЛБИКЕ, А НЕ РЯДОМ С НИМ.

            Не в каждой странице по отдельности: страниц девять,
            и половина из них начинается с раннего возврата
            («читаю тариф…», «нет доступа») — на этих состояниях
            кнопки бы не было, хотя экран человек видит.

            Но и не голым ребёнком `<main>`: у шести экранов
            `<main>` — РЯД с выравниванием по центру, и вторая
            вещь в этом ряду отбирает у содержимого своё место.
            На 390 сверка 26.08 намерила ровно семьдесят два
            отобранных пикселя. Довод целиком — `screen.ts`,
            «СТОЛБИК ЭКРАНА».
            ═══════════════════════════════════════════════════ */}
        <ВСтолбике столбик={столбикЭкрана(location.pathname)}>
          {назад !== null && (
            <Назад
              to={назад === 'кампанию' ? кампанияПуть : домПуть}
              куда={назад === 'кампанию' && выбраннаяКампания === null ? 'биддер' : назад}
            />
          )}
          <Outlet />
        </ВСтолбике>
      </main>
    </div>
  );
}

/**
 * Обернуть содержимое экрана в столбик — или не оборачивать.
 *
 * ЛИШНЕГО УЗЛА У ЭКРАНОВ-ПОЛОС НЕ ПОЯВЛЯЕТСЯ: там `столбик`
 * равен `null`, и дети уходят в `<main>` как были. Заворачивать
 * всех подряд «для единообразия» значило бы поставить между
 * коробкой и содержимым лишний слой на пяти экранах из тринадцати
 * — и однажды искать, почему у полосы не работает `flex: 1`.
 */
function ВСтолбике({ столбик, children }: { столбик: Столбик | null; children: ReactNode }) {
  if (столбик === null) return <>{children}</>;
  return (
    <div
      className="flex w-full flex-col"
      style={{
        maxWidth: столбик.maxWidth,
        gap: столбик.gap,
        /**
         * ═══════════════════════════════════════════════════════════
         * СТОЛБИК РОСТОМ В СВОЁ СОДЕРЖИМОЕ, А НЕ В ОКНО.
         *
         * `<main>` — гибкий РЯД, и по умолчанию он растягивает детей
         * по высоте (`align-items: stretch`). Столбик получал ростом
         * ровно окно, а содержимого в нём бывало больше — и тогда
         * ВСЕ ЕГО ДЕТИ СЖИМАЛИСЬ: у гибкого столбика `flex-shrink`
         * работает по высоте.
         *
         * Как это выглядело. Кнопка «Назад» ростом 40 (палец) —
         * двадцать. Карточка счёта 358 — 296. Требования к паролю
         * 124 — 98. Пузырь сообщения 202 — 176. Всё это НЕ ЛОВИЛОСЬ
         * сверкой ширин и не выглядело поломкой: просто «у нас
         * потеснее, чем в дизайне», на каждом экране понемногу.
         *
         * И зависело от ВЫСОТЫ ОКНА: на 1600 пикселях всё было
         * на месте, на 1400 — сжато. То есть на ноутбуке криво,
         * а на большом мониторе ровно.
         *
         * `flex-start` по поперечной оси — это «ростом в содержимое».
         * Прокрутка остаётся у `<main>`, как и была.
         * ═══════════════════════════════════════════════════════ */
        alignSelf: 'flex-start',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Имя кабинета для глаз.
 *
 * Порядок предпочтений не произволен: имя продавца из WB человек узнаёт
 * сразу, торговую марку — тоже, а номер продавца он видит у себя в кабинете
 * WB и называет в поддержке. Внутренний идентификатор не говорит ему ничего,
 * поэтому он последний.
 */
/** То же имя, но от целого кабинета: чтобы не разбирать поля на месте. */
function cabinetTitle(c: {
  displayName: string | null;
  tradeMark: string | null;
  wbOwnerId: number | null;
}): string {
  return cabinetName(c.displayName, c.tradeMark, c.wbOwnerId);
}


function NoAccess({ what }: { what: string }) {
  return (
    <div className="p-6">
      <div className="rk-panel max-w-md p-5">
        <h1 className="mb-2 font-semibold">Доступа нет</h1>
        <p className="mb-4 rk-text-body rk-faint">
          Это {what} вам не открыто. Ссылка могла прийти из другого пространства, либо доступ
          отозвали.
        </p>
        <Link className="rk-link" to="/rk">
          К своим кабинетам
        </Link>
      </div>
    </div>
  );
}

/**
 * «1 обращение», «2 обращения», «5 обращений».
 *
 * Здесь, а не в общем месте: это единственное слово в кабинете,
 * которое склоняется по такому образцу, и вынести его значило бы
 * завести общий склонятель ради одного вызова.
 */
function склонениеОбращений(n: number): string {
  const последняя = n % 10;
  const подросток = n % 100 >= 11 && n % 100 <= 14;
  if (подросток) return 'обращений';
  if (последняя === 1) return 'обращение';
  if (последняя >= 2 && последняя <= 4) return 'обращения';
  return 'обращений';
}
